/**
 * The reason column — the one that makes an access log worth reading.
 *
 * `API_CONTRACT.md` §8.10: *"Dòng `data_access` mang `reason` … Đây là điểm khiến
 * access log có giá trị; thiếu nó thì chỉ là một danh sách tên."*
 *
 * `web/src/audit/reason.ts` turns the pair `(log_type, reason)` into one of four
 * facts. This renders them, and the thing worth checking is that **none of the four
 * is a blank cell**:
 *
 *  - the two "no reason" branches are sentences, not `—`, because an empty cell in a
 *    column headed *lý do* reads as "the reader declined to say", which puts an
 *    innocent person under suspicion in an audit report;
 *  - the two "has a reason" branches print the operator's own words **verbatim**, in
 *    quotation marks, and are labelled differently — "why I looked" and "why I did
 *    this" are different claims about different acts.
 *
 * `data-reason-kind` carries the discriminant, so a mutation that swaps one branch
 * for its neighbour dies on the attribute rather than needing four Vietnamese
 * sentences grepped out of the DOM — and so that the assertion cannot be satisfied by
 * a neighbouring element, which is how two survivors got through on Tasks 45 and 53.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { reasonOf } from '../../audit/reason';
import type { AuditLogEntry } from '../../api/types';

export interface ReasonCellProps {
  readonly entry: AuditLogEntry;
}

export function ReasonCell({ entry }: ReasonCellProps): ReactNode {
  const { t } = useI18n();
  const reason = reasonOf(entry);
  const testId = `k8-audit-reason-${entry.id}`;

  if (reason.kind === 'access_reason') {
    return (
      <span data-testid={testId} data-reason-kind={reason.kind}>
        {t('k8.audit.reason.access', { text: reason.text })}
      </span>
    );
  }
  if (reason.kind === 'action_reason') {
    return (
      <span data-testid={testId} data-reason-kind={reason.kind}>
        {t('k8.audit.reason.action', { text: reason.text })}
      </span>
    );
  }
  if (reason.kind === 'access_not_gated') {
    return (
      <span className="wp-small wp-muted" data-testid={testId} data-reason-kind={reason.kind}>
        {t('k8.audit.reason.accessNotGated')}
      </span>
    );
  }
  return (
    <span className="wp-small wp-muted" data-testid={testId} data-reason-kind={reason.kind}>
      {t('k8.audit.reason.actionNeedsNone')}
    </span>
  );
}
